import React, { useCallback } from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as Styles from './FastCheckoutCart.styles';
import {
  Accordion, Button, IconInfo, IconSecurityV2, Tag,
  Tooltip,
} from 'gatorcomponents';
import { DISCOUNT_TYPES, FAST_CHECKOUT_CREDIT_CARD_METHODS } from '@/pages/FastCheckout/FastCheckout.enum.jsx';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { FastCheckoutUserData } from './FastCheckoutUserData';
import { FastCheckoutPayment } from './FastCheckoutPayment';

const FastCheckoutCart = ({
  paymentMethod,
  onFinalize,
  renderIframe,
  productCycle,
  currentCreditCard,
  isCcButtonLoading,
  isCcButtonEnabled,
  isMobile,
  creditCardMethod,
  setCreditCardMethod,
  locale,
  product,
  hasDiscount,
  summaryState,
  enableFastcheckoutPlg,
  handleFormChange,
  formData,
  validateField,
  countryCode,
  countriesSelectOptions,
  isForeigner,
  setIsForeigner,
  brand,
  loadingZipCode,
  isCompany,
  isBRCountry,
  disableUserDataFields,
  isIframeLoaded,
}) => {
  const {
    productName, productIcon, productIsTrial, productDiscount, productBasePrice, productFinalPrice, productRenewalDate, productVat,
  } = product;

  const { title: toolTipTitle, description: toolTipDescription, topics: toolTipTopics } = locale.taxToolTip;

  const iframeInLoading = creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW && !isIframeLoaded;

  const handleFinalize = useCallback((event) => {
    onFinalize && onFinalize(event);
  }, [onFinalize]);

  const finalizeBlock = (
    <>
      <Styles.ButtonWrapper>
        <Button disabled={!isCcButtonEnabled} loading={isCcButtonLoading} onClick={handleFinalize} width="full" size="large" label={locale.ctaPurchase} />
      </Styles.ButtonWrapper>
      <Styles.SecurityReminderWrapper>
        <IconSecurityV2 size="sm" />
        <Styles.SecurityReminder>{locale.securityReminder}</Styles.SecurityReminder>
      </Styles.SecurityReminderWrapper>
    </>
  );

  const taxToolTipContent = (
    <Styles.TaxToolTipWrapper>
      <Styles.TaxToolTipTitle>
        {toolTipTitle}
      </Styles.TaxToolTipTitle>
      <Styles.TaxToolTipContent>
        {toolTipDescription}
      </Styles.TaxToolTipContent>
      <Styles.TaxToolTipList>
        {toolTipTopics.map(topic => (
          <Styles.TaxToolTipListItem key={topic.id}>
            {topic.text}
          </Styles.TaxToolTipListItem>
        ))}
      </Styles.TaxToolTipList>
    </Styles.TaxToolTipWrapper>
  );

  const emailAccordionInfo = () => (
    <Styles.EmailAccordionWrapper>
      <Styles.Email>{summaryState.email}</Styles.Email>
    </Styles.EmailAccordionWrapper>
  );

  return (
    <Styles.Content>
      <Styles.Cart>
        {!isMobile && (
          <Styles.CartTitle>
            {locale.cartTitle}
          </Styles.CartTitle>
        )}
        <Styles.AccordionsWrapper>
          {enableFastcheckoutPlg && (
            <>
              <Styles.AccordionAdapter>
                <Accordion
                  titleSize="small"
                  open
                  items={emailAccordionInfo}
                  withShadow={false}
                  withBorder
                  width="full"
                  leftIcon="IconEmailV2"
                  title={locale.emailTitle}
                  allowToggle={false}
                />
              </Styles.AccordionAdapter>
              <Styles.AccordionAdapter>
                <Accordion
                  titleSize="small"
                  open
                  items={() => (
                    <FastCheckoutUserData
                      brand={brand}
                      countriesSelectOptions={countriesSelectOptions}
                      countryCode={countryCode}
                      disableUserDataFields={disableUserDataFields}
                      formData={formData}
                      handleFormChange={handleFormChange}
                      isBRCountry={isBRCountry}
                      isCompany={isCompany}
                      isForeigner={isForeigner}
                      loadingZipCode={loadingZipCode}
                      locale={locale.personalDataForm}
                      setIsForeigner={setIsForeigner}
                      validateField={validateField}
                    />
                  )}
                  chevronColor="primaryMedium"
                  withShadow={false}
                  withBorder
                  width="full"
                  leftIcon="IconPerson"
                  iconPositionStatic
                  title={locale.dataTitle}
                />
              </Styles.AccordionAdapter>
            </>
          )}
          <Styles.AccordionAdapter>
            <Accordion
              titleSize="small"
              open
              items={() => (
                <FastCheckoutPayment
                  paymentMethod={paymentMethod}
                  renderIframe={renderIframe}
                  currentCreditCard={currentCreditCard}
                  setCreditCardMethod={setCreditCardMethod}
                  creditCardMethod={creditCardMethod}
                  productIsTrial={productIsTrial}
                  finalizeBlock={finalizeBlock}
                  productRenewalDate={productRenewalDate}
                  locale={locale}
                  isMobile={isMobile}
                  isIframeLoaded={isIframeLoaded}
                  iframeInLoading={iframeInLoading}
                />
              )}
              chevronColor="primaryMedium"
              withShadow={false}
              withBorder
              width="full"
              leftIcon="IconCard"
              iconPositionStatic
              title={locale.paymentTitle}
            />
          </Styles.AccordionAdapter>
        </Styles.AccordionsWrapper>
      </Styles.Cart>
      <Styles.Resume>
        {isMobile && (
          <Styles.CartTitle>
            {locale.cartTitle}
          </Styles.CartTitle>
        )}
        <Styles.ResumeTitle>
          {locale.resumeTitle}
        </Styles.ResumeTitle>
        <Styles.ProductDetailsBox>
          <Styles.ProductTitle>{locale.product}</Styles.ProductTitle>
          <Styles.TagWrapper>
            <Tag variant="info" label={locale.cycles[productCycle]} />
          </Styles.TagWrapper>
          <Styles.ProductNameWrapper>
            <Styles.ProductName>{productName}</Styles.ProductName>
            {productIcon && <Styles.ProductIcon src={productIcon} />}
          </Styles.ProductNameWrapper>
          {hasDiscount && (
            <Styles.BasePriceWrapper>
              <Tag variant="success" label={`${locale.discountTag} ${productDiscount.type === DISCOUNT_TYPES.PERCENTAGE ? productDiscount.percentage : formatCurrency(productDiscount.value)}`} />
              <Styles.ProductBasePriceWrapper>
                <Styles.ProductBasePrice>
                  {locale.value}
                </Styles.ProductBasePrice>
                <Styles.ProductBasePrice>
                  {formatCurrency(productBasePrice)}
                </Styles.ProductBasePrice>
              </Styles.ProductBasePriceWrapper>
            </Styles.BasePriceWrapper>
          )}
          {productVat.length > 0 && (
            <Styles.TaxesWrapper>
              {productVat.map(vat => (
                <Styles.ProductTaxWrapper key={vat.name}>
                  <Styles.ProductTax>
                    {vat.name}
                    <Styles.IconInfoWrapper>
                      <Tooltip content={taxToolTipContent} preferredPosition={isMobile ? 'bottomRight' : 'bottomLeft'}>
                        <IconInfo size="sm" color="primaryMedium" />
                      </Tooltip>
                    </Styles.IconInfoWrapper>
                  </Styles.ProductTax>
                  <Styles.ProductTax>
                    {formatCurrency(vat.value)}
                  </Styles.ProductTax>
                </Styles.ProductTaxWrapper>
              ))}
            </Styles.TaxesWrapper>
          )}
          <Styles.ProductFinalPriceWrapper>
            <Styles.ProductFinalPrice>{locale.total}</Styles.ProductFinalPrice>
            <Styles.ProductFinalPrice>{formatCurrency(productFinalPrice)}</Styles.ProductFinalPrice>
          </Styles.ProductFinalPriceWrapper>
          <Styles.ProductRenewal>
            {`${locale.renewal} ${productRenewalDate}`}
          </Styles.ProductRenewal>
        </Styles.ProductDetailsBox>
        {!isMobile && finalizeBlock}
      </Styles.Resume>
    </Styles.Content>
  );
};
export default withErrorBoundary(FastCheckoutCart, 'FastCheckoutCart');
